import React from "react";
import { NavLink, useLocation, useParams } from "react-router-dom";
import { usePatient } from "../context/PatientContext";
import { useTheme } from "../context/ThemeContext";
import { motion } from "framer-motion";
import {
  ClipboardCheck,
  Stethoscope,
  History,
  Search,
  AlertCircle,
  Users,
  Activity,
  Thermometer,
  FileEdit,
  Pill,
  CreditCard,
  CalendarCheck,
  CheckCircle2
} from "lucide-react";

const PatientSidebar = ({ completedSections = {} }) => {
  const location = useLocation();
  const { theme, isDarkMode } = useTheme();
  const { patientId, caseId, visitId } = useParams();
  const { patientData } = usePatient();

  const sections = [
    { name: "Patient Info", path: "", icon: ClipboardCheck },
    { name: "Symptoms", path: "symptoms", icon: Thermometer },
    { name: "History", path: "history", icon: History },
    { name: "Clinical Investigation", path: "ci", icon: Search },
    { name: "Anyavyadhi", path: "anyavyadhi", icon: AlertCircle },
    { name: "Kulawrutta", path: "kulawrutta", icon: Users },
    { name: "Tapasanya", path: "tapasanya", icon: Activity },
    { name: "Artawawrutta", path: "artawawrutta", icon: Stethoscope },
    { name: "Diagnosis", path: "diagnosis", icon: FileEdit },
    { name: "Treatment", path: "treatment", icon: Pill },
    { name: "Payment", path: "payment", icon: CreditCard },
    { name: "Next Visit", path: "nextvisit", icon: CalendarCheck },
  ];

  const patientName = patientData?.patientInfo?.[0]?.PATIENT_NAME || "New Patient";
  const caseTitle = patientData?.caseInfo?.[0]?.CASE_TITLE || "Case Enrollment";

  return (
    <div className="h-full flex flex-col">
      <div className="p-4 md:p-8 border-b" style={{ borderColor: theme.border }}>
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <h3 className="text-xl md:text-2xl font-serif mb-1 truncate" style={{ color: theme.text, fontFamily: theme.fonts?.serif }}>
            {patientName}
          </h3>
          <div className="flex items-center gap-2 mb-2 md:mb-3">
            <span className="text-[9px] md:text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded bg-primary/10" style={{ color: theme.primary, backgroundColor: theme.primary + '20' }}>
              ID: {patientId || "NEW"}
            </span>
            <span className="text-[9px] md:text-[10px] font-semibold opacity-50 uppercase tracking-widest" style={{ color: theme.textSecondary }}>
              Medical File
            </span>
          </div>
          <p className="text-[10px] md:text-xs font-medium opacity-60 italic" style={{ color: theme.textSecondary }}>
            {caseTitle}
          </p>
        </motion.div>
      </div>

      <nav className="flex-1 overflow-y-auto p-4 space-y-1 custom-scrollbar">
        {sections.map((section, index) => {
          let fullPath = "";
          if (location.pathname.startsWith(`/visits/${patientId}/${caseId}/${visitId}`)) {
            fullPath = `/visits/${patientId}/${caseId}/${visitId}/${section.path}`.replace(/\/$/, "");
          } else if (location.pathname.startsWith("/addpatient")) {
            fullPath = `/addpatient/${section.path}`.replace(/\/$/, "");
          }

          if (!fullPath) return null;

          const isActive = location.pathname === fullPath || (section.path === "" && (location.pathname === "/addpatient" || location.pathname === `/visits/${patientId}/${caseId}/${visitId}`));
          const Icon = section.icon;
          const isCompleted = completedSections[section.path || "info"];

          return (
            <motion.div
              key={index}
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: index * 0.05 }}
            >
              <NavLink
                to={fullPath}
                className={`flex items-center justify-between px-3 md:px-4 py-2 md:py-3 rounded-xl transition-all duration-300 group ${isActive ? "shadow-md" : "hover:bg-opacity-50"
                  }`}
                style={{
                  backgroundColor: isActive ? theme.primary : "transparent",
                  color: isActive ? "#ffffff" : theme.text,
                }}
              >
                <div className="flex items-center gap-2 md:gap-3">
                  <Icon
                    size={18}
                    className={`transition-colors duration-300 ${isActive ? "text-white" : "opacity-60 group-hover:opacity-100"}`}
                    style={{ color: isActive ? "#ffffff" : theme.primary }}
                  />
                  <span className={`text-xs md:text-sm font-semibold tracking-wide ${isActive ? "text-white" : "opacity-80"}`}>
                    {section.name}
                  </span>
                </div>
                {isCompleted && !isActive && (
                  <CheckCircle2 size={16} className="text-success" style={{ color: theme.success }} />
                )}
                {isActive && (
                  <motion.div
                    layoutId="active-indicator"
                    className="w-1.5 h-1.5 rounded-full bg-white"
                  />
                )}
              </NavLink>
            </motion.div>
          );
        })}
      </nav>

      <div className="p-6 border-t mt-auto text-center" style={{ borderColor: theme.border }}>
        <p className="text-[10px] font-bold uppercase tracking-[0.2em] opacity-30" style={{ color: theme.text }}>
          Amrutdhara Healthcare
        </p>
      </div>
    </div>
  );
};

export default PatientSidebar;

